import { CreateStudentSchema, type CreateStudent, type StudentDto } from '@mm/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createStudent, getStudent, updateStudent } from '../api/students';
import { AppLayout } from '../layout/AppLayout';
import { Button } from '../ui/Button';
import { Field } from '../ui/Field';
import { Input } from '../ui/Input';

type FormState = { fullName: string; email: string; phone: string; grade: string; notes: string };

const EMPTY: FormState = { fullName: '', email: '', phone: '', grade: '', notes: '' };

export function StudentFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [studentName, setStudentName] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    getStudent(id).then((s: StudentDto) => {
      setForm({
        fullName: s.fullName,
        email: s.email ?? '',
        phone: s.phone ?? '',
        grade: s.grade ?? '',
        notes: s.notes ?? '',
      });
      setStudentName(s.fullName);
      setLoading(false);
    });
  }, [id, isEdit]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    const raw: Record<string, string | null> = {
      fullName: form.fullName.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      grade: form.grade.trim() || null,
      notes: form.notes.trim() || null,
    };
    const parsed = CreateStudentSchema.safeParse(raw);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      parsed.error.issues.forEach((i) => {
        errs[String(i.path[0])] = i.message;
      });
      setErrors(errs);
      return;
    }
    setBusy(true);
    try {
      if (isEdit && id) await updateStudent(id, parsed.data as CreateStudent);
      else await createStudent(parsed.data as CreateStudent);
      nav('/app/students');
    } catch {
      setErrors({ _form: 'Не удалось сохранить. Попробуйте ещё раз.' });
    } finally {
      setBusy(false);
    }
  }

  const crumbs = isEdit
    ? [
        { label: 'Админ' },
        { to: '/app/students', label: 'Ученики' },
        ...(id ? [{ to: `/app/students/${id}`, label: studentName ?? '…' }] : [{ label: studentName ?? '…' }]),
        { label: 'Редактирование' },
      ]
    : [{ label: 'Админ' }, { to: '/app/students', label: 'Ученики' }, { label: 'Новый' }];

  return (
    <AppLayout crumbs={crumbs}>
      <div className="max-w-xl px-8 py-7">
        {loading ? (
          <p className="text-muted text-[13px]">Загрузка…</p>
        ) : (
          <>
            <h1
              className="font-display text-[38px] leading-none font-medium mb-1"
              style={{ fontVariationSettings: '"opsz" 96, "SOFT" 5' }}
            >
              {isEdit ? 'Редактировать' : 'Новый ученик'}
            </h1>
            <p className="font-caps italic text-[13px] text-muted mb-8">
              {isEdit ? 'Обновите данные подопечного' : 'Добавьте подопечного в свою базу'}
            </p>

            <form onSubmit={onSubmit} className="space-y-5">
              <Field label="ФИО" error={errors.fullName}>
                <Input
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  required
                  autoFocus
                  placeholder="Иван Петров"
                />
              </Field>
              <Field label="Класс" error={errors.grade} hint="Необязательно">
                <Input
                  value={form.grade}
                  onChange={(e) => setForm({ ...form, grade: e.target.value })}
                  placeholder="8А"
                />
              </Field>
              <Field label="Email" error={errors.email} hint="Необязательно">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="ivan@example.com"
                />
              </Field>
              <Field label="Телефон" error={errors.phone} hint="Необязательно">
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+7 …"
                />
              </Field>
              <Field label="Заметки" error={errors.notes}>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={5}
                  className="flex w-full rounded border border-border bg-bg px-3 py-2 text-[13px] text-fg placeholder:text-muted focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-ring/20"
                  placeholder="Темы, успехи, пробелы, расписание…"
                />
              </Field>
              {errors._form ? <p className="text-[12px] text-accent">{errors._form}</p> : null}
              <div className="flex gap-2 pt-4 border-t border-border">
                <Button type="submit" disabled={busy}>
                  {busy ? 'Сохраняем…' : 'Сохранить'}
                </Button>
                <Link to={isEdit && id ? `/app/students/${id}` : '/app/students'}>
                  <Button type="button" variant="outline">
                    Отмена
                  </Button>
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </AppLayout>
  );
}
